import type {
  CartBillingContext,
  CartItem,
  QuoteCartFinancialSummary,
  QuoteCartSnapshotUpdatePayload,
  QuoteSentToCartPayload,
} from './types'

type CartQuoteOrigin = NonNullable<QuoteSentToCartPayload['originQuote']>

export type CartQuoteCheckoutRequestContext = {
  orcamentoId?: number
  quoteMode?: CartBillingContext['quoteMode']
  useCartItems?: boolean
}

export type CartQuoteTaxSnapshotItem = {
  subtotal?: number | null
  discount?: number | null
  impostos?: number | null
  ipi?: number | null
  difal?: number | null
}

const roundMoneyAmount = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100

export const applyCartQuoteTaxSnapshot = (
  items: CartItem[],
  taxItems: Array<CartQuoteTaxSnapshotItem | null | undefined>,
): CartItem[] => items.map((item, index) => {
  const taxItem = taxItems[index]
  if (!taxItem) return item

  const quantity = Math.max(0, Number(item.quantity) || 0)
  const unitPrice = Math.max(0, Number(item.lockedUnitPrice) || 0)
  const subtotal = roundMoneyAmount(Math.max(
    0,
    Number(taxItem.subtotal) || Number(item.lockedSubtotal) || unitPrice * quantity,
  ))
  const discount = roundMoneyAmount(Math.max(
    0,
    Number(taxItem.discount) || Number(item.lockedDiscount) || 0,
  ))
  const ipi = roundMoneyAmount(Math.max(0, Number(taxItem.ipi) || 0))
  const difal = roundMoneyAmount(Math.max(0, Number(taxItem.difal) || 0))
  const taxes = roundMoneyAmount(Math.max(
    0,
    Number(taxItem.impostos) || ipi + difal,
  ))

  return {
    ...item,
    lockedSubtotal: subtotal,
    lockedDiscount: discount,
    lockedIpi: ipi,
    lockedDifal: difal,
    lockedTaxes: taxes,
    lockedTotal: roundMoneyAmount(Math.max(0, subtotal - discount + taxes)),
  }
})

export const buildCartQuoteCheckoutRequestContext = (
  billingContext: CartBillingContext | null | undefined,
  isCurrentQuoteSnapshot: boolean,
): CartQuoteCheckoutRequestContext => {
  if (
    !billingContext
    || billingContext.destination !== 'client'
    || !isCurrentQuoteSnapshot
  ) return {}

  return {
    orcamentoId: billingContext.originQuoteId,
    quoteMode: billingContext.quoteMode,
    useCartItems: true,
  }
}

const normalizeSignaturePart = (value: unknown) => String(value ?? '')
  .trim()
  .toLocaleUpperCase('pt-BR')

export const buildCartQuoteSnapshotSignature = (items: CartItem[]) => items
  .map((item) => {
    const productId = normalizeSignaturePart(
      item.product.code || item.product.cartBaseProductId || item.product.id
    )
    const packagingQuantity = Math.max(1, Math.trunc(Number(item.product.packagingQuantity) || 1))
    const quantity = Math.max(0, Number(item.quantity) || 0)
    return `${productId}:${packagingQuantity}:${quantity}`
  })
  .filter(Boolean)
  .sort()
  .join('|')

export const doesCartMatchQuoteFinancialSummary = (
  items: CartItem[],
  financialSummary: QuoteCartFinancialSummary | null | undefined,
) => {
  const summaryItems = Array.isArray(financialSummary?.items)
    ? financialSummary.items
    : []
  if (items.length === 0 || summaryItems.length !== items.length) return false

  const remainingSummaryItems = [...summaryItems]
  return items.every((item) => {
    const productCode = normalizeSignaturePart(item.product.code)
    const productId = Math.max(0, Math.trunc(Number(
      item.product.cartBaseProductId ?? item.product.id
    ) || 0))
    const packagingQuantity = Math.max(
      1,
      Math.trunc(Number(item.product.packagingQuantity) || 1),
    )
    const quantity = Math.max(0, Number(item.quantity) || 0)
    const matchingIndex = remainingSummaryItems.findIndex((summaryItem) => {
      const summaryCode = normalizeSignaturePart(summaryItem.productCode)
      const summaryProductId = Math.max(0, Math.trunc(Number(summaryItem.productId) || 0))
      const summaryProductGradeId = Math.max(
        0,
        Math.trunc(Number(summaryItem.productGradeId) || 0),
      )
      const summaryPackagingQuantity = Math.max(
        1,
        Math.trunc(Number(summaryItem.packagingQuantity) || 1),
      )
      const sameProduct = productCode && summaryCode
        ? productCode === summaryCode
        : productId > 0
          && (productId === summaryProductId || productId === summaryProductGradeId)

      return Boolean(
        sameProduct
        && packagingQuantity === summaryPackagingQuantity
        && Math.abs(quantity - (Number(summaryItem.quantity) || 0)) < 0.0001
      )
    })

    if (matchingIndex < 0) return false
    remainingSummaryItems.splice(matchingIndex, 1)
    return true
  })
}

export const applyLinkedQuoteCartSnapshotUpdate = (
  currentOrigin: CartQuoteOrigin | null,
  currentCartItems: CartItem[],
  payload: QuoteCartSnapshotUpdatePayload,
) => {
  if (
    !currentOrigin
    || currentOrigin.context !== 'business'
    || currentOrigin.quoteMode !== 'partner'
    || currentOrigin.quoteId !== payload.quoteId
    || !currentOrigin.cartSnapshotSignature
  ) {
    return currentOrigin
  }

  const currentCartSignature = buildCartQuoteSnapshotSignature(currentCartItems)
  const updatedQuoteSignature = buildCartQuoteSnapshotSignature(payload.cartItems)
  const currentCartMatches = currentOrigin.cartSnapshotSignature === currentCartSignature
    || doesCartMatchQuoteFinancialSummary(currentCartItems, currentOrigin.financialSummary)
  const updatedQuoteMatches = currentOrigin.cartSnapshotSignature === updatedQuoteSignature
    || doesCartMatchQuoteFinancialSummary(payload.cartItems, payload.financialSummary)
  if (!currentCartMatches || !updatedQuoteMatches) {
    return currentOrigin
  }

  return {
    ...currentOrigin,
    deliveryAddressId: Number(payload.deliveryAddressId ?? 0) > 0
      ? Math.trunc(Number(payload.deliveryAddressId))
      : null,
    freightPrefill: payload.freightPrefill,
    financialSummary: payload.financialSummary,
  }
}
